/**
* @license Apache-2.0
*
* Copyright (c) 2025 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/*
* When adding modules to the namespace, ensure that they are added in alphabetical order according to module name.
*/

// MODULES //

import setReadOnly from '@stdlib/utils/define-read-only-property';


// MAIN //

/**
* Namespace.
*
* @namespace ns
*/
var ns = {};

/**
* @name max
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/max}
*/
import max from './../../array/max';
setReadOnly( ns, 'max', max );

/**
* @name maxBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/max-by}
*/
import maxBy from './../../array/max-by';
setReadOnly( ns, 'maxBy', maxBy );

/**
* @name maxabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/maxabs}
*/
import maxabs from './../../array/maxabs';
setReadOnly( ns, 'maxabs', maxabs );

/**
* @name maxsorted
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/maxsorted}
*/
import maxsorted from './../../array/maxsorted';
setReadOnly( ns, 'maxsorted', maxsorted );

/**
* @name mean
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/mean}
*/
import mean from './../../array/mean';
setReadOnly( ns, 'mean', mean );

/**
* @name meankbn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/meankbn}
*/
import meankbn from './../../array/meankbn';
setReadOnly( ns, 'meankbn', meankbn );

/**
* @name meankbn2
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/meankbn2}
*/
import meankbn2 from './../../array/meankbn2';
setReadOnly( ns, 'meankbn2', meankbn2 );

/**
* @name meanors
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/meanors}
*/
import meanors from './../../array/meanors';
setReadOnly( ns, 'meanors', meanors );

/**
* @name meanpn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/meanpn}
*/
import meanpn from './../../array/meanpn';
setReadOnly( ns, 'meanpn', meanpn );

/**
* @name meanpw
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/meanpw}
*/
import meanpw from './../../array/meanpw';
setReadOnly( ns, 'meanpw', meanpw );

/**
* @name meanwd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/meanwd}
*/
import meanwd from './../../array/meanwd';
setReadOnly( ns, 'meanwd', meanwd );

/**
* @name mediansorted
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/mediansorted}
*/
import mediansorted from './../../array/mediansorted';
setReadOnly( ns, 'mediansorted', mediansorted );

/**
* @name min
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/min}
*/
import min from './../../array/min';
setReadOnly( ns, 'min', min );

/**
* @name minBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/min-by}
*/
import minBy from './../../array/min-by';
setReadOnly( ns, 'minBy', minBy );

/**
* @name minabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/minabs}
*/
import minabs from './../../array/minabs';
setReadOnly( ns, 'minabs', minabs );

/**
* @name minsorted
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/minsorted}
*/
import minsorted from './../../array/minsorted';
setReadOnly( ns, 'minsorted', minsorted );

/**
* @name mskmax
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/mskmax}
*/
import mskmax from './../../array/mskmax';
setReadOnly( ns, 'mskmax', mskmax );

/**
* @name mskmin
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/mskmin}
*/
import mskmin from './../../array/mskmin';
setReadOnly( ns, 'mskmin', mskmin );

/**
* @name mskrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/mskrange}
*/
import mskrange from './../../array/mskrange';
setReadOnly( ns, 'mskrange', mskrange );

/**
* @name nanmax
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/nanmax}
*/
import nanmax from './../../array/nanmax';
setReadOnly( ns, 'nanmax', nanmax );

/**
* @name nanmaxBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/nanmax-by}
*/
import nanmaxBy from './../../array/nanmax-by';
setReadOnly( ns, 'nanmaxBy', nanmaxBy );

/**
* @name nanmaxabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/nanmaxabs}
*/
import nanmaxabs from './../../array/nanmaxabs';
setReadOnly( ns, 'nanmaxabs', nanmaxabs );

/**
* @name nanmean
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/nanmean}
*/
import nanmean from './../../array/nanmean';
setReadOnly( ns, 'nanmean', nanmean );

/**
* @name nanmeanors
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/nanmeanors}
*/
import nanmeanors from './../../array/nanmeanors';
setReadOnly( ns, 'nanmeanors', nanmeanors );

/**
* @name nanmeanpn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/nanmeanpn}
*/
import nanmeanpn from './../../array/nanmeanpn';
setReadOnly( ns, 'nanmeanpn', nanmeanpn );

/**
* @name nanmeanwd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/nanmeanwd}
*/
import nanmeanwd from './../../array/nanmeanwd';
setReadOnly( ns, 'nanmeanwd', nanmeanwd );

/**
* @name nanmin
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/nanmin}
*/
import nanmin from './../../array/nanmin';
setReadOnly( ns, 'nanmin', nanmin );

/**
* @name nanminBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/nanmin-by}
*/
import nanminBy from './../../array/nanmin-by';
setReadOnly( ns, 'nanminBy', nanminBy );

/**
* @name nanminabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/nanminabs}
*/
import nanminabs from './../../array/nanminabs';
setReadOnly( ns, 'nanminabs', nanminabs );

/**
* @name nanmskmax
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/nanmskmax}
*/
import nanmskmax from './../../array/nanmskmax';
setReadOnly( ns, 'nanmskmax', nanmskmax );

/**
* @name nanmskmin
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/nanmskmin}
*/
import nanmskmin from './../../array/nanmskmin';
setReadOnly( ns, 'nanmskmin', nanmskmin );

/**
* @name nanmskrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/nanmskrange}
*/
import nanmskrange from './../../array/nanmskrange';
setReadOnly( ns, 'nanmskrange', nanmskrange );

/**
* @name nanrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/nanrange}
*/
import nanrange from './../../array/nanrange';
setReadOnly( ns, 'nanrange', nanrange );

/**
* @name nanrangeBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/nanrange-by}
*/
import nanrangeBy from './../../array/nanrange-by';
setReadOnly( ns, 'nanrangeBy', nanrangeBy );

/**
* @name nanstdev
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/nanstdev}
*/
import nanstdev from './../../array/nanstdev';
setReadOnly( ns, 'nanstdev', nanstdev );

/**
* @name nanstdevch
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/nanstdevch}
*/
import nanstdevch from './../../array/nanstdevch';
setReadOnly( ns, 'nanstdevch', nanstdevch );

/**
* @name nanstdevpn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/nanstdevpn}
*/
import nanstdevpn from './../../array/nanstdevpn';
setReadOnly( ns, 'nanstdevpn', nanstdevpn );

/**
* @name nanstdevtk
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/nanstdevtk}
*/
import nanstdevtk from './../../array/nanstdevtk';
setReadOnly( ns, 'nanstdevtk', nanstdevtk );

/**
* @name nanstdevwd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/nanstdevwd}
*/
import nanstdevwd from './../../array/nanstdevwd';
setReadOnly( ns, 'nanstdevwd', nanstdevwd );

/**
* @name nanstdevyc
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/nanstdevyc}
*/
import nanstdevyc from './../../array/nanstdevyc';
setReadOnly( ns, 'nanstdevyc', nanstdevyc );

/**
* @name nanvariance
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/nanvariance}
*/
import nanvariance from './../../array/nanvariance';
setReadOnly( ns, 'nanvariance', nanvariance );

/**
* @name nanvariancech
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/nanvariancech}
*/
import nanvariancech from './../../array/nanvariancech';
setReadOnly( ns, 'nanvariancech', nanvariancech );

/**
* @name nanvariancepn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/nanvariancepn}
*/
import nanvariancepn from './../../array/nanvariancepn';
setReadOnly( ns, 'nanvariancepn', nanvariancepn );

/**
* @name nanvariancetk
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/nanvariancetk}
*/
import nanvariancetk from './../../array/nanvariancetk';
setReadOnly( ns, 'nanvariancetk', nanvariancetk );

/**
* @name nanvariancewd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/nanvariancewd}
*/
import nanvariancewd from './../../array/nanvariancewd';
setReadOnly( ns, 'nanvariancewd', nanvariancewd );

/**
* @name nanvarianceyc
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/nanvarianceyc}
*/
import nanvarianceyc from './../../array/nanvarianceyc';
setReadOnly( ns, 'nanvarianceyc', nanvarianceyc );

/**
* @name range
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/range}
*/
import range from './../../array/range';
setReadOnly( ns, 'range', range );

/**
* @name rangeBy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/range-by}
*/
import rangeBy from './../../array/range-by';
setReadOnly( ns, 'rangeBy', rangeBy );

/**
* @name stdev
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/stdev}
*/
import stdev from './../../array/stdev';
setReadOnly( ns, 'stdev', stdev );

/**
* @name stdevch
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/stdevch}
*/
import stdevch from './../../array/stdevch';
setReadOnly( ns, 'stdevch', stdevch );

/**
* @name stdevpn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/stdevpn}
*/
import stdevpn from './../../array/stdevpn';
setReadOnly( ns, 'stdevpn', stdevpn );

/**
* @name stdevtk
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/stdevtk}
*/
import stdevtk from './../../array/stdevtk';
setReadOnly( ns, 'stdevtk', stdevtk );

/**
* @name stdevwd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/stdevwd}
*/
import stdevwd from './../../array/stdevwd';
setReadOnly( ns, 'stdevwd', stdevwd );

/**
* @name stdevyc
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/stdevyc}
*/
import stdevyc from './../../array/stdevyc';
setReadOnly( ns, 'stdevyc', stdevyc );

/**
* @name variance
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/variance}
*/
import variance from './../../array/variance';
setReadOnly( ns, 'variance', variance );

/**
* @name variancech
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/variancech}
*/
import variancech from './../../array/variancech';
setReadOnly( ns, 'variancech', variancech );

/**
* @name variancepn
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/variancepn}
*/
import variancepn from './../../array/variancepn';
setReadOnly( ns, 'variancepn', variancepn );

/**
* @name variancetk
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/variancetk}
*/
import variancetk from './../../array/variancetk';
setReadOnly( ns, 'variancetk', variancetk );

/**
* @name variancewd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/variancewd}
*/
import variancewd from './../../array/variancewd';
setReadOnly( ns, 'variancewd', variancewd );

/**
* @name varianceyc
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/array/varianceyc}
*/
import varianceyc from './../../array/varianceyc';
setReadOnly( ns, 'varianceyc', varianceyc );


// EXPORTS //

export default ns;
